import React, { useState } from 'react';

import { X } from 'lucide-react';
import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import { addManualCandidate } from '../api';
import { CANDIDATES_QUERY_KEY, CATEGORY_OPTIONS, RENDER_SLOT_OPTIONS } from '../constants';
import { CandidateRenderSlot, FeatureCategory, ManualCandidatePayload } from '../types';

type AddCandidateFormProps = {
  editionId: string;
  onClose: () => void;
  onAdded: () => void;
};

const AddCandidateForm: React.FC<AddCandidateFormProps> = ({ editionId, onClose, onAdded }) => {
  const [fields, setFields] = useState<ManualCandidatePayload>({
    headline: '',
    benefit: '',
    cta_label: '',
    cta_url: '',
    render_slot: 'card',
    category: null,
  });

  const update = <K extends keyof ManualCandidatePayload>(
    key: K,
    value: ManualCandidatePayload[K]
  ): void => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const [submit, { isLoading }] = useMutation(() => addManualCandidate(editionId, fields), {
    onSuccess: () => {
      void queryCache.invalidateQueries([CANDIDATES_QUERY_KEY, editionId]);
      Toast.success('Candidato agregado.');
      onAdded();
      onClose();
    },
    onError: () => {
      Toast.error('Error al agregar el candidato.');
    },
  });

  const canSubmit = fields.headline.trim().length > 0 && fields.benefit.trim().length > 0;

  return (
    <div className="bg-blue-50 p-4 space-y-3 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-blue-800">Nuevo candidato manual</h4>
        <button
          type="button"
          className="btn btn-ghost hover:text-gray-600 p-1 text-gray-400"
          onClick={onClose}
        >
          <X size={14} />
        </button>
      </div>

      <label className="block">
        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">Título *</span>
        <input
          className="input mt-0.5 w-full text-sm"
          value={fields.headline}
          placeholder="Título del feature"
          onChange={(e) => update('headline', e.target.value)}
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          Descripción / beneficio *
        </span>
        <textarea
          className="input mt-0.5 w-full text-sm resize-none"
          rows={3}
          value={fields.benefit}
          placeholder="¿Qué gana el cliente?"
          onChange={(e) => update('benefit', e.target.value)}
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            CTA label
          </span>
          <input
            className="input mt-0.5 w-full text-sm"
            value={fields.cta_label ?? ''}
            placeholder="Conoce más"
            onChange={(e) => update('cta_label', e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">CTA URL</span>
          <input
            className="input mt-0.5 w-full text-sm"
            type="url"
            value={fields.cta_url ?? ''}
            placeholder="https://..."
            onChange={(e) => update('cta_url', e.target.value)}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">Slot</span>
          <select
            className="input mt-0.5 w-full text-sm"
            value={fields.render_slot ?? 'card'}
            onChange={(e) => update('render_slot', e.target.value as CandidateRenderSlot)}
          >
            {RENDER_SLOT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Categoría
          </span>
          <select
            className="input mt-0.5 w-full text-sm"
            value={fields.category ?? ''}
            onChange={(e) => update('category', (e.target.value as FeatureCategory) || null)}
          >
            <option value="">Sin categoría</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn btn-ghost px-3 py-1 text-sm" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary px-3 py-1 text-sm"
          disabled={!canSubmit || isLoading}
          onClick={() => submit()}
        >
          {isLoading ? 'Agregando…' : 'Agregar candidato'}
        </button>
      </div>
    </div>
  );
};

export default AddCandidateForm;
